// Frontend API service for billing/monetization
import api from '../api';
import {
  checkoutQuoteRequiredError,
  quoteIdFrom,
} from './checkoutQuote';

/**
 * Get public pricing configuration (no auth required)
 */
export const getPricingConfig = async () => {
  const response = await api.get('/billing/config');
  return response.data;
};

/** Fixed organization membership; no requisition quote or count is involved. */
export const getPremiumOffer = async ({ fresh = false } = {}) => {
  if (fresh && typeof api.invalidateCache === 'function') {
    api.invalidateCache('/billing/premium-offer');
  }
  const response = await api.get('/billing/premium-offer');
  return response.data;
};

export const startPremiumCheckout = async ({ planId, successUrl, cancelUrl }) => {
  if (!planId) throw new Error('Review the current Premium plan before continuing.');
  const response = await api.post('/billing/premium-checkout', {
    planId,
    ...(successUrl ? { successUrl } : {}),
    ...(cancelUrl ? { cancelUrl } : {}),
  });
  return response.data;
};

/**
 * Legacy contracted pricing only. Fixed Premium uses getPremiumOffer.
 *
 * Premium is priced per account from its current billing-policy basis, so this,
 * and not the global `monthlyPrice` in /billing/config, is the amount Checkout
 * charges.
 */
const freshGroupQuoteRequests = new Map();

export const getGroupQuote = async ({ fresh = false } = {}) => {
  // A checkout path that somehow received a quote without an id gets one
  // explicit retry. Bust the two-second shared cache first so that retry is a
  // real read rather than the same incomplete response.
  if (fresh) {
    // `invalidateCache` intentionally detaches in-flight reads. Coalesce the
    // explicit refreshes here first so two missing-id recovery paths cannot
    // invalidate each other and recreate the original race one layer lower.
    // Keep the coalescer account-scoped just like the axios cache. A rapid
    // logout/login must never let the new account inherit the old account's
    // in-flight quote.
    const authToken = localStorage.getItem('authToken') || '';
    const existing = freshGroupQuoteRequests.get(authToken);
    if (existing) return existing;
    if (typeof api.invalidateCache === 'function') {
      api.invalidateCache('/billing/quote');
    }
    const request = api
      .get('/billing/quote')
      .then((response) => response.data)
      .finally(() => {
        if (freshGroupQuoteRequests.get(authToken) === request) {
          freshGroupQuoteRequests.delete(authToken);
        }
      });
    freshGroupQuoteRequests.set(authToken, request);
    return request;
  }
  const response = await api.get('/billing/quote');
  return response.data;
};

/**
 * Legacy recruiting-readiness metadata. This endpoint does not gate the
 * current Billing path or checkout.
 */
export const getPremiumEligibility = async () => {
  const response = await api.get('/billing/premium-eligibility');
  return response.data;
};

/**
 * Get current user's egg balance
 */
export const getEggBalance = async () => {
  const response = await api.get('/billing/balance');
  return response.data;
};

/**
 * Check if user can perform an action
 */
export const canPerformAction = async (action) => {
  const response = await api.post('/billing/can-perform', { action });
  return response.data;
};

/**
 * Spend eggs on an action
 */
export const spendEggs = async (action, entityId, metadata = {}) => {
  const response = await api.post('/billing/spend', {
    action,
    entityId,
    metadata,
  });
  return response.data;
};

/**
 * Create subscription upgrade checkout session.
 *
 * The third argument is either the quote id this checkout is agreeing to, or
 * an options object with that required `quoteId` plus optional success/cancel
 * URLs. Passing a bare string is what the billing card reads best; collapsing
 * both shapes into one positional slot keeps a single function rather than
 * two that drift.
 *
 * The quote id lets the server tell that the pricing basis moved between the
 * quote the customer read and the click. Without it the server cannot refuse
 * with QUOTE_REFRESHED, and the customer is billed on a price they never saw.
 */
export const upgradeSubscription = async (
  targetTier,
  billingPeriod,
  quoteIdOrOptions = {},
) => {
  const options =
    typeof quoteIdOrOptions === 'string'
      ? { quoteId: quoteIdOrOptions }
      : quoteIdOrOptions || {};
  const quoteId = quoteIdFrom(options);
  if (!quoteId) throw checkoutQuoteRequiredError();

  const response = await api.post('/billing/subscription/upgrade', {
    targetTier,
    billingPeriod,
    quoteId,
    ...(options.successUrl ? { successUrl: options.successUrl } : {}),
    ...(options.cancelUrl ? { cancelUrl: options.cancelUrl } : {}),
  });
  return response.data;
};

/**
 * This employer account's live Premium subscription summary.
 *
 * Separate from /billing/balance because the card needs the locked cycle, the
 * next renewal, and the outcome counts together; the balance route answers a
 * different question and stitching the two client-side would let them disagree.
 */
export const getSubscriptionSummary = async () => {
  const response = await api.get('/billing/subscription');
  return response.data;
};

/**
 * Sync subscription status from Stripe
 * Called after user returns from checkout to ensure local DB is updated
 */
export const syncSubscription = async () => {
  const response = await api.post('/billing/subscription/sync');
  return response.data;
};

/**
 * Create a price-safe Stripe Customer Portal session.
 *
 * Per-account Premium prices can only change through RxRooster's quoted
 * renewal flow. The portal is therefore limited to payment methods, invoices,
 * and cancellation; it must never offer Stripe-side plan or price switching.
 */
export const openCustomerPortal = async () => {
  const response = await api.post('/billing/subscription/portal');
  return response.data;
};

// Action constants for type safety
export const SpendAction = {
  UNLOCK_CONTACT: 'UNLOCK_CONTACT',
  BOOST_LISTING: 'BOOST_LISTING',
  FEATURED_LISTING: 'FEATURED_LISTING',
  COLD_MESSAGE: 'COLD_MESSAGE',
};

export default {
  getPricingConfig,
  getPremiumOffer,
  startPremiumCheckout,
  getGroupQuote,
  getSubscriptionSummary,
  getEggBalance,
  canPerformAction,
  spendEggs,
  upgradeSubscription,
  syncSubscription,
  openCustomerPortal,
  SpendAction,
};
